import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CockpitSidebar } from "./CockpitSidebar";
import MachineInfo from "./MachineInfo";
import MachinePicture from "./MachinePicture";
import MachineFlowChain from "./MachineFlowChain";
import type { Machine } from "../services/BackendDataService";
import LineInfo from "./LineInfo";
import {
  headerButtonClass,
  Icon,
  OrgLogo,
  Popover,
  useBranding,
  useMediaQuery,
} from "@digitalisation/enlixy-comp";
import { useTour } from "@digitalisation/enlixy-comp/onboarding";
import { HelpButton } from "../help/Onboarding";
import { CockpitMark } from "./CockpitMark";
import { CockpitUserMenu } from "./CockpitUserMenu";

import SearchField from "./SearchField";
import { useLiveLineStatus } from "../hooks/useLiveLineStatus";
import { useAssistantUi } from "./cockpit/assistant/assistantUiStore";
import { headerStyles, cx } from "../styles";

// Language options with flag emojis and full names
const LANGUAGE_OPTIONS = [
  { code: "en", flag: "🇺🇸", name: "English", shortName: "EN" },
  { code: "de", flag: "🇩🇪", name: "Deutsch", shortName: "DE" },
  { code: "fr", flag: "🇫🇷", name: "Français", shortName: "FR" },
];

/**
 * Header heights (px). The top bar holds menu, title, search and language
 * (two rows on phones); machine and line pages add their card below it. On
 * a scrolled machine or line page the header is cut (clip-path, so the page
 * does not jump) to the bar plus the card's one-line row.
 */
const HEADER_HEIGHT = {
  bar: { mobile: 104, desktop: 80 },
  withCard: { mobile: 200, desktop: 280 },
  compact: { mobile: 144, desktop: 120 },
} as const;

/**
 * Height of an organization's logo (per-org branding) in the 80 px bar by
 * its branding size s / m / l: 32 / 44 / 56 px (the package's 24 / 32 /
 * 40 px suit its 64 px header). Arbitrary variants on the wrapper, so they
 * win over the height and width classes of OrgLogo's <img>.
 */
const ORG_LOGO_HEIGHT = {
  s: "[&_img]:h-8",
  m: "[&_img]:h-11",
  l: "[&_img]:h-14",
} as const;

/**
 * Language: a round header button with the language code, like the help
 * button and the avatar next to it; the list opens in the shared Popover
 * (rendered over the header, which clips its content).
 */
function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const current =
    LANGUAGE_OPTIONS.find((lang) => i18n.language?.startsWith(lang.code)) ||
    LANGUAGE_OPTIONS[0];
  const label = `${t("languageMenu")}: ${current.name}`;
  return (
    <Popover
      label={label}
      width={200}
      triggerClassName={cx(headerButtonClass(), "text-xs font-semibold")}
      trigger={() => <span aria-hidden="true">{current.shortName}</span>}
    >
      {(close) => (
        <div role="radiogroup" aria-label={t("languageMenu")} className="p-1.5 text-sm">
          {LANGUAGE_OPTIONS.map((option) => {
            const active = option.code === current.code;
            return (
              <button
                key={option.code}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  void i18n.changeLanguage(option.code);
                  close();
                }}
                className={cx(
                  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-hover",
                  active && "font-semibold",
                )}
              >
                <span aria-hidden="true">{option.flag}</span>
                <span className="flex-1">{option.name}</span>
                {active && (
                  <Icon name="check" size={15} className="text-accent dark:text-accent-on-dark" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </Popover>
  );
}

// Top bar: menu, company, icons
function HeaderTopBar({
  currentView: _currentView,
  menuOpen,
  onMenuToggle,
  height,
}: {
  currentView: string;
  menuOpen: boolean;
  onMenuToggle: (open: boolean) => void;
  /** px, HEADER_HEIGHT.bar */
  height: number;
}) {
  const navigate = useNavigate();
  const [searchExpanded, setSearchExpanded] = useState(false);
  // From 1280 px (xl) the bar has room for the Focke logo (~240 px) in the
  // middle, as in the other Enlixy apps, with the 320 px search moved next to
  // the language selector. Narrower bars keep the search in the middle and the
  // logo on the right.
  const logoCentered = useMediaQuery("(min-width: 1280px)");
  // Created once and placed by `logoCentered`, so the desktop bar has a single
  // SearchField (own state, listeners, portal dropdown) and a single logo.
  const searchField = <SearchField />;
  // The organization's logo (per-org branding), else the Focke logo in the
  // header's text colour.
  const branding = useBranding();
  const orgLogo = !!branding?.logoUrl;
  const orgLogoHeight = ORG_LOGO_HEIGHT[branding?.logoSize ?? "m"];
  const logo = <OrgLogo />;

  const handleMenuToggle = () => {
    const newMenuOpen = !menuOpen;
    onMenuToggle(newMenuOpen);
  };

  const handleTitleClick = () => {
    navigate("/");
  };

  // Animated Hamburger Icon component
  function HamburgerIcon({ open }: { open: boolean }) {
    return (
      <span className={headerStyles.hamburger.icon}>
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.top
              : headerStyles.hamburger.states.closed.top,
          )}
        />
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.middle
              : headerStyles.hamburger.states.closed.middle,
          )}
        />
        <span
          className={cx(
            headerStyles.hamburger.line,
            open
              ? headerStyles.hamburger.states.open.bottom
              : headerStyles.hamburger.states.closed.bottom,
          )}
        />
      </span>
    );
  }

  // Removed RefreshButton component - was for testing purposes

  return (
    <div
      className={cx(
        headerStyles.topBar.container,
        "px-1 sm:px-4 md:px-8 lg:px-10",
      )}
      style={{ zIndex: 100, height }}
    >
      {/* Mobile: Compact Layout */}
      <div className="flex flex-col gap-2 md:hidden w-full py-1">
        {/* Top row: Hamburger + Title + Language */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className={headerStyles.hamburger.button}
              onClick={handleMenuToggle}
              aria-label="Open menu"
              data-tour="menu-button"
            >
              <HamburgerIcon open={menuOpen} />
            </button>
            <h1
              className={cx(
                headerStyles.topBar.title,
                "cursor-pointer hover:opacity-80 transition-opacity duration-200",
                "flex items-center gap-2 text-sm",
              )}
              onClick={handleTitleClick}
            >
              <CockpitMark className="h-7 w-7 shrink-0" />
              Cockpit
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <HelpButton />
            <LanguageSelector />
            <CockpitUserMenu />
          </div>
        </div>

        {/* Bottom row: Search */}
        <div className="flex items-center gap-2 w-full">
          <div
            className={searchExpanded ? "flex-1" : "flex-shrink-0"}
            data-tour="header-search"
          >
            <SearchField onExpandedChange={setSearchExpanded} />
          </div>
        </div>
      </div>

      {/* Desktop: Horizontal Layout */}
      <div className="hidden md:contents">
        {/* Left: Hamburger Menu and Performance Cockpit Title */}
        <div className={cx(headerStyles.topBar.left, "flex-shrink-0")}>
          {/* from lg up the menu rail opens the menu (its top button) */}
          <button
            className={cx(headerStyles.hamburger.button, "lg:hidden")}
            onClick={handleMenuToggle}
            aria-label="Open menu"
            data-tour="menu-button"
          >
            <HamburgerIcon open={menuOpen} />
          </button>
          <h1
            className={cx(
              headerStyles.topBar.title,
              "cursor-pointer hover:opacity-80 transition-opacity duration-200",
              "flex items-center gap-2.5 text-sm sm:text-base md:text-lg lg:text-xl",
            )}
            onClick={handleTitleClick}
          >
            <CockpitMark className="h-9 w-9 shrink-0" />
            <span className="hidden sm:inline">Performance Cockpit</span>
            <span className="sm:hidden">Cockpit</span>
          </h1>
        </div>

        {/* Center: search field, or from 1280 px the Focke logo */}
        {logoCentered ? (
          <span
            // an organization's logo is an <img> with its own alt text
            {...(orgLogo ? {} : { role: "img", "aria-label": "Focke & Co" })}
            className={cx(
              "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-on-header [&>svg]:ml-0 [&>svg]:h-9",
              orgLogoHeight,
              "[&_img]:max-w-[300px]",
            )}
          >
            {logo}
          </span>
        ) : (
          <div
            className={headerStyles.topBar.center}
            style={{ zIndex: 100 }}
            data-tour="header-search"
          >
            {searchField}
          </div>
        )}

        {/* Right: search (from 1280 px), help, language selector, logo (below) */}
        <div className={cx(headerStyles.topBar.right, "flex-shrink-0")}>
          {/* text-base undoes this group's text-2xl for the search input */}
          {logoCentered && (
            <div className="text-base" data-tour="header-search">
              {searchField}
            </div>
          )}
          <HelpButton />
          <LanguageSelector />
          {!logoCentered && (
            <div
              className={cx(
                "hidden md:flex items-center",
                orgLogoHeight,
                "[&_img]:max-w-[200px]",
              )}
            >
              {logo}
            </div>
          )}
          <CockpitUserMenu />
        </div>
      </div>
    </div>
  );
}

function MachineCard({
  scrolled,
  machineData,
}: {
  scrolled: boolean;
  machineData?: {
    machineId?: string;
    machineName?: string;
    lineId?: string;
    lineName?: string;
    lineNumber?: string;
    machineType?: string;
    factory?: string;
  };
}) {
  const navigate = useNavigate();
  // The open KI-Assistent drawer covers the bottom right corner.
  const assistantOpen = useAssistantUi((s) => s.isOpen);

  // Format line display: show lineNumber with lineName if both available
  const lineDisplay =
    machineData?.lineNumber && machineData?.lineName
      ? `${machineData.lineNumber} - ${machineData.lineName}`
      : machineData?.lineNumber ||
        machineData?.lineName ||
        machineData?.lineId ||
        "TP01";

  // Strip the redundant machine number from the pre-formatted name
  // ("20055 - Modell 350 (20055)" → "Modell 350") for the compact subtitle.
  const escId = String(machineData?.machineId ?? "").replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );
  const compactModel = escId
    ? (machineData?.machineName || "")
        .replace(new RegExp(`^\\s*${escId}\\s*-\\s*`), "")
        .replace(new RegExp(`\\s*\\(${escId}\\)\\s*$`), "")
        .trim()
    : machineData?.machineName || "";

  const handleLineClick = () => {
    if (machineData?.lineId) {
      navigate(`/line/${machineData.lineId}`, {
        state: {
          lineName: machineData.lineName,
          lineNumber: machineData.lineNumber,
          factory: machineData.factory,
        },
      });
    }
  };

  return (
    <div
      data-tour="machine-card"
      className={cx(
        headerStyles.machineCard.base,
        scrolled
          ? headerStyles.machineCard.scrolled
          : headerStyles.machineCard.normal,
      )}
      style={{ willChange: "transform, opacity, min-height" }}
    >
      {/* Info left */}
      <div className={headerStyles.machineCard.info.container}>
        {scrolled ? (
          // Compact horizontal layout when scrolled
          <div className={headerStyles.machineCard.info.compact}>
            <div className={headerStyles.machineCard.info.title}>
              {machineData?.machineId || "56881"}
            </div>
            <div className={headerStyles.machineCard.info.subtitle}>
              {compactModel && <span>{compactModel}</span>}
              <span
                onClick={handleLineClick}
                className="cursor-pointer hover:text-on-header hover:underline transition-colors"
              >
                Line: {lineDisplay}
              </span>
            </div>
          </div>
        ) : (
          // Full layout when not scrolled
          <MachineInfo {...machineData} />
        )}
      </div>
      {/* Picture positioned slightly below center - only visible when not scrolled and on larger screens */}
      {!scrolled && (
        <div
          className={cx(
            headerStyles.machineCard.picture.container,
            "-translate-x-1/2 -translate-y-1/2 transition-all duration-300 ease-out",
            "hidden md:block",
          )}
        >
          <MachinePicture
            key={`${machineData?.machineId}-${machineData?.machineType}`}
            machineId={machineData?.machineId}
            machineType={machineData?.machineType}
          />
        </div>
      )}
      {/* Spacer for layout, button is now absolutely positioned */}
      <div className="hidden md:block" />
      <ServicePortalLink scrolled={scrolled} hidden={assistantOpen} />
    </div>
  );
}

function LineCard({
  scrolled,
  lineData,
}: {
  scrolled: boolean;
  lineData?: {
    lineId?: string;
    lineNumber?: string;
    lineName?: string;
    factory?: string;
    machineCount?: number;
    lineType?: string;
    supervisor?: string;
    machines?: Machine[];
    oemNumbers?: string[];
  };
}) {
  // The open KI-Assistent drawer covers the bottom right corner.
  const assistantOpen = useAssistantUi((s) => s.isOpen);

  // Get live NATS status for all machines in the line
  const machineIds = React.useMemo(
    () =>
      (lineData?.machines || [])
        .map((m) => m.maschinennummer || m.maschineBK || "")
        .filter(Boolean),
    [lineData?.machines],
  );
  const liveStatuses = useLiveLineStatus(machineIds);
  return (
    <div
      className={cx(
        headerStyles.lineCard.base,
        scrolled
          ? headerStyles.lineCard.scrolled
          : headerStyles.lineCard.normal,
      )}
      style={{ willChange: "transform, opacity, min-height" }}
    >
      {/* Info left */}
      <div className={headerStyles.lineCard.info.container}>
        {scrolled ? (
          <div className={headerStyles.lineCard.info.compact}>
            <div className={headerStyles.lineCard.info.title}>
              {lineData?.lineNumber || lineData?.lineId || "N/A"}
            </div>
            <div className={headerStyles.lineCard.info.subtitle}>
              {lineData?.lineName && <span>{lineData.lineName}</span>}
              <span>Machines: {lineData?.machineCount || 4}</span>
            </div>
          </div>
        ) : (
          <LineInfo {...lineData} />
        )}
      </div>
      {/* Machine flow chain - centered on desktop (absolute), below info on mobile (flow) */}
      {!scrolled && lineData?.machines && lineData.machines.length > 0 && (
        <>
          {/* Mobile: below line info, scrollable */}
          <div className="w-full flex justify-center mt-2 md:hidden">
            <MachineFlowChain
              machines={lineData.machines}
              lineId={lineData.lineId}
              lineName={lineData.lineName}
              factory={lineData.factory}
              liveStatuses={liveStatuses}
            />
          </div>
          {/* Desktop: absolutely centered */}
          <div className="absolute inset-0 hidden md:flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <MachineFlowChain
                machines={lineData.machines}
                lineId={lineData.lineId}
                lineName={lineData.lineName}
                factory={lineData.factory}
                liveStatuses={liveStatuses}
              />
            </div>
          </div>
        </>
      )}
      {/* Spacer for layout, button is now absolutely positioned */}
      <div className="hidden md:block" />
      <ServicePortalLink scrolled={scrolled} hidden={assistantOpen} />
    </div>
  );
}

/**
 * Service Portal link at the bottom right of the machine or line card; in
 * the scrolled header's one-line row it is centred in the row. Hidden on
 * phones and while the KI-Assistent drawer is open (it covers that corner).
 */
function ServicePortalLink({
  scrolled,
  hidden,
}: {
  scrolled: boolean;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  return (
    <a
      href="https://serviceportal.focke.com/de/login"
      target="_blank"
      rel="noopener noreferrer"
      className={cx(
        headerStyles.servicePortal.style,
        headerStyles.servicePortal.hover,
        "absolute right-8 z-50 hidden",
        scrolled ? "top-1/2 -translate-y-1/2 py-1.5" : "bottom-1 py-3",
        !hidden && "md:block",
      )}
    >
      {t("servicePortal")}
    </a>
  );
}

const Header: React.FC<{
  currentView: string;
  machineData?: {
    machineId?: string;
    machineName?: string;
    lineId?: string;
    lineName?: string;
    lineNumber?: string;
    machineType?: string;
    factory?: string;
  };
  lineData?: {
    lineId?: string;
    lineNumber?: string;
    lineName?: string;
    factory?: string;
    machineCount?: number;
    lineType?: string;
    supervisor?: string;
    machines?: Machine[];
  };
}> = ({ currentView, machineData, lineData }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // A guided tour highlights parts of the page: the menu would cover them.
  const tourActive = useTour().active;
  useEffect(() => {
    if (tourActive) setMenuOpen(false);
  }, [tourActive]);
  // Same breakpoint as before (window.innerWidth < 768).
  const isMobile = useMediaQuery("(max-width: 767.98px)");

  // Show machine info only on machine view
  const showMachineCard = currentView === "machine";
  // Show line info only on line view
  const showLineCard = currentView === "line";
  const showAnyCard = showMachineCard || showLineCard;

  // Track previous view to detect changes
  const [prevView, setPrevView] = useState(currentView);

  // Track when cards should be visible with animation
  const [cardsVisible, setCardsVisible] = useState(showAnyCard);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setScrolled(scrollY > 30);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Publish the header's visible height as `--app-header-height`.
   *
   * Anything that wants to stick *below* the header needs this, and it can't be
   * hard-coded: the height depends on whether a machine/line card is shown, on
   * the breakpoint, and on scroll position — once scrolled with a card the
   * clip-path crops the header to 120px, so its visible height and its box height
   * differ. Height, clip and this value all come from HEADER_HEIGHT.
   */
  const device = isMobile ? "mobile" : "desktop";
  const compact = scrolled && showAnyCard;
  const fullHeight = showAnyCard
    ? HEADER_HEIGHT.withCard[device]
    : HEADER_HEIGHT.bar[device];
  const visibleHeight = compact ? HEADER_HEIGHT.compact[device] : fullHeight;
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-header-height",
      `${visibleHeight}px`,
    );
  }, [visibleHeight]);

  // Handle initial mount - if we start with cards, show them immediately
  useEffect(() => {
    if (prevView === currentView && showAnyCard) {
      setCardsVisible(true);
    }
  }, []);

  // Sync cardsVisible with showAnyCard for initial render
  useEffect(() => {
    // If cards should be visible and previous view equals current view (no transition), show them
    if (showAnyCard && prevView === currentView && !cardsVisible) {
      setCardsVisible(true);
    }
  }, [showAnyCard, cardsVisible, prevView, currentView]);

  // Animate header height when view changes
  useEffect(() => {
    // Only animate if view actually changed
    if (currentView !== prevView) {
      // Update previous view
      setPrevView(currentView);

      // If contracting (hiding cards), hide cards immediately
      if (!showAnyCard) {
        setCardsVisible(false);
      }
      // If expanding (showing cards), show cards after a brief delay
      else {
        // Start with cards hidden during expansion
        setCardsVisible(false);
        // Show cards after a brief delay
        const timer = setTimeout(() => {
          setCardsVisible(true);
        }, 200);

        return () => clearTimeout(timer);
      }
    }
  }, [currentView, showAnyCard, prevView]);

  return (
    <>
      <div className={headerStyles.stickyWrapper}>
      <div
        ref={headerRef}
        className={headerStyles.container}
        style={{
          // tokens.css (light and dark), or the organization's branding
          background: "var(--header-gradient)",
          height: fullHeight,
          // Scrolled on a machine or line page: cut to the bar and the
          // card's one-line row. inset() rounds the cut like the header's
          // bottom (a polygon left it square).
          clipPath: `inset(0 0 ${fullHeight - visibleHeight}px 0 round 0 0 var(--header-radius) var(--header-radius))`,
          transition:
            "clip-path 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), height 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)",
        }}
      >
        <HeaderTopBar
          currentView={currentView}
          menuOpen={menuOpen}
          onMenuToggle={setMenuOpen}
          height={HEADER_HEIGHT.bar[device]}
        />
        {showMachineCard && (
          <div
            className={cx(
              "transition-all duration-500 ease-out flex-1 flex flex-col min-h-0",
              scrolled ? "justify-start" : "justify-center",
            )}
            style={{
              transform: cardsVisible ? "translateY(0)" : "translateY(-20px)",
              opacity: cardsVisible ? 1 : 0,
            }}
          >
            <MachineCard scrolled={scrolled} machineData={machineData} />
          </div>
        )}
        {showLineCard && (
          <div
            className={cx(
              "transition-all duration-500 ease-out flex-1 flex flex-col min-h-0",
              scrolled ? "justify-start" : "justify-center",
            )}
            style={{
              transform: cardsVisible ? "translateY(0)" : "translateY(-20px)",
              opacity: cardsVisible ? 1 : 0,
            }}
          >
            <LineCard scrolled={scrolled} lineData={lineData} />
          </div>
        )}
      </div>
      </div>
      <CockpitSidebar
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
      />
    </>
  );
};

export default Header;
